import {
  Check,
  Clipboard,
  Download,
  KeyRound,
  LogOut,
  Plus,
  ShieldCheck,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { api, signOut } from "../api.js";
import type { Me } from "../types.js";

const allPermissions = [
  ["jobs:read", "读取职位"],
  ["jobs:write", "写入职位、招聘状态与标签"],
  ["assessments:write", "写入 Agent 评估"],
  ["comments:write", "写入评论"],
  ["exports:read", "导出工作区"],
] as const;

interface ApiKeyItem {
  id: string;
  name: string | null;
  start: string | null;
  enabled: boolean;
  permissions: string[];
  expires_at: string | null;
  created_at: string;
  last_used_at: string | null;
}

export function SettingsPage({ me, refreshMe }: { me: Me; refreshMe: () => Promise<void> }) {
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [name, setName] = useState("Local Codex");
  const [permissions, setPermissions] = useState<string[]>([
    "jobs:read",
    "jobs:write",
    "assessments:write",
    "comments:write",
  ]);
  const [expires, setExpires] = useState("90");
  const [revealed, setRevealed] = useState<{ key: string; name: string } | null>(null);
  const [error, setError] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");

  const loadKeys = () =>
    api<{ items: ApiKeyItem[] }>("/api/internal/api-keys").then((data) => setKeys(data.items));
  useEffect(() => {
    void loadKeys();
  }, []);

  function togglePermission(permission: string) {
    setPermissions((current) =>
      current.includes(permission) ? current.filter((item) => item !== permission) : [...current, permission],
    );
  }

  async function createKey(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      const result = await api<{ key: string; name: string }>("/api/internal/api-keys", {
        method: "POST",
        body: JSON.stringify({ name, permissions, expires_in_days: expires ? Number(expires) : null }),
      });
      setRevealed(result);
      await loadKeys();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法创建 API Key。");
    }
  }

  async function revokeKey(id: string) {
    await api(`/api/internal/api-keys/${id}`, { method: "DELETE" });
    await loadKeys();
  }

  async function invite(event: FormEvent) {
    event.preventDefault();
    const result = await api<{ invitation_url: string }>("/api/internal/invitations", {
      method: "POST",
      body: JSON.stringify({ email: inviteEmail, expires_in_days: 7 }),
    });
    setInviteUrl(result.invitation_url);
  }

  async function logout() {
    await signOut();
    await refreshMe();
  }

  return (
    <main className="settings-page" id="main-content">
      <header className="settings-header">
        <p className="eyebrow">个人工作区</p>
        <h1>设置与 Agent API</h1>
        <p>控制本地 Agent 的访问范围，管理邀请，并导出你的资料。</p>
      </header>
      <div className="settings-layout">
        <nav className="settings-nav" aria-label="设置分区">
          <a href="#agent-access">Agent 访问</a>
          <a href="#data-export">数据导出</a>
          {me.user.role.includes("admin") && <a href="#invitations">用户邀请</a>}
          <a href="#account">账号</a>
        </nav>
        <div className="settings-content">
          <section id="agent-access" className="settings-section">
            <div className="settings-section-heading">
              <div>
                <p className="eyebrow">Agent 访问</p>
                <h2>创建有限权限的 API Key</h2>
                <p>密钥只在创建时显示一次。RoleWorth 只保存不可还原的哈希。</p>
              </div>
              <KeyRound />
            </div>
            {revealed && (
              <div className="secret-reveal" role="status">
                <div>
                  <ShieldCheck />
                  <span>
                    <strong>{revealed.name} 已创建</strong>
                    <small>现在复制并保存，离开后无法再次查看。</small>
                  </span>
                </div>
                <code>{revealed.key}</code>
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => void navigator.clipboard.writeText(revealed.key)}
                >
                  <Clipboard />
                  复制密钥
                </button>
              </div>
            )}
            <form className="key-form" onSubmit={createKey}>
              <div className="form-grid">
                <label>
                  密钥名称
                  <input
                    required
                    maxLength={100}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </label>
                <label>
                  有效期
                  <select value={expires} onChange={(event) => setExpires(event.target.value)}>
                    <option value="30">30 天</option>
                    <option value="90">90 天</option>
                    <option value="365">1 年</option>
                    <option value="">永不过期</option>
                  </select>
                </label>
              </div>
              <fieldset>
                <legend>权限</legend>
                <div className="permission-grid">
                  {allPermissions.map(([permission, label]) => (
                    <label key={permission}>
                      <input
                        type="checkbox"
                        checked={permissions.includes(permission)}
                        onChange={() => togglePermission(permission)}
                      />
                      <span>
                        <Check />
                        {label}
                        <small>{permission}</small>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              <button className="button primary" type="submit" disabled={!permissions.length}>
                <Plus />
                创建 API Key
              </button>
            </form>
            <div className="key-list">
              <div className="key-list-head">
                <span>现有密钥</span>
                <span>{keys.filter((key) => key.enabled).length} 个可用</span>
              </div>
              {keys.length ? (
                keys.map((key) => (
                  <article className="key-row" key={key.id}>
                    <div className={`key-status ${key.enabled ? "is-live" : ""}`} />
                    <div>
                      <strong>{key.name ?? "未命名 Agent"}</strong>
                      <code>{key.start ?? "rw_live_"}••••••••</code>
                      <small>{key.permissions.join(" · ") || "没有权限"}</small>
                    </div>
                    <div className="key-dates">
                      <span>创建 {new Date(key.created_at).toLocaleDateString("zh-CN")}</span>
                      <span>
                        {key.last_used_at
                          ? `最后使用 ${new Date(key.last_used_at).toLocaleString("zh-CN")}`
                          : "尚未使用"}
                      </span>
                    </div>
                    {key.enabled ? (
                      <button
                        className="icon-button danger"
                        type="button"
                        aria-label={`撤销 ${key.name} 密钥`}
                        onClick={() => void revokeKey(key.id)}
                      >
                        <Trash2 />
                      </button>
                    ) : (
                      <span className="revoked-label">已撤销</span>
                    )}
                  </article>
                ))
              ) : (
                <p className="empty-setting">还没有 Agent API Key。</p>
              )}
            </div>
            <div className="api-example">
              <div>
                <strong>API 地址</strong>
                <code>{window.location.origin}/api/v1</code>
              </div>
              <pre>
                <code>{`curl -H "x-api-key: $ROLEWORTH_API_KEY" \\\n  "${window.location.origin}/api/v1/jobs?limit=10"`}</code>
              </pre>
            </div>
          </section>
          <section id="data-export" className="settings-section">
            <div className="settings-section-heading">
              <div>
                <p className="eyebrow">可移植性</p>
                <h2>导出工作区</h2>
                <p>JSON 包含职位和全部历史，CSV 适合表格查看当前状态。</p>
              </div>
              <Download />
            </div>
            <div className="export-actions">
              <a className="button secondary" href="/api/v1/exports/jobs?format=json" download>
                下载 JSON
              </a>
              <a className="button secondary" href="/api/v1/exports/jobs?format=csv" download>
                下载 CSV
              </a>
            </div>
          </section>
          {me.user.role.includes("admin") && (
            <section id="invitations" className="settings-section">
              <div className="settings-section-heading">
                <div>
                  <p className="eyebrow">仅限管理员</p>
                  <h2>邀请新用户</h2>
                  <p>邀请链接 7 天有效，每个新用户自动获得隔离的个人工作区。</p>
                </div>
                <UserPlus />
              </div>
              <form className="invite-form" onSubmit={invite}>
                <label>
                  受邀邮箱
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(event) => setInviteEmail(event.target.value)}
                  />
                </label>
                <button className="button primary" type="submit">
                  生成邀请
                </button>
              </form>
              {inviteUrl && (
                <div className="invite-result">
                  <code>{inviteUrl}</code>
                  <button
                    className="button secondary"
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(inviteUrl)}
                  >
                    <Clipboard />
                    复制链接
                  </button>
                </div>
              )}
            </section>
          )}
          <section id="account" className="settings-section">
            <div className="settings-section-heading">
              <div>
                <p className="eyebrow">登录会话</p>
                <h2>{me.user.name}</h2>
                <p>
                  {me.workspace.name} · {me.user.role.includes("admin") ? "管理员" : "所有者"}
                </p>
              </div>
            </div>
            <button className="button secondary" type="button" onClick={() => void logout()}>
              <LogOut />
              退出登录
            </button>
          </section>
        </div>
      </div>
    </main>
  );
}
